/**
 * Workspace Context Manager (Phase 2)
 *
 * Manages the activeWorkspaces map that tracks which workspaces are loaded and authenticated.
 * Replaces the single activeWorkspaceId with a multi-workspace model.
 */

export type WorkspaceAuthState = 'authenticated' | 'pending' | 'locked';

export interface ActiveWorkspace {
  workspaceId: string;
  profileId: string;
  authState: WorkspaceAuthState;
  profileName?: string;
  workspaceName?: string;
  lastAccessed?: number; // Timestamp
}

const STORAGE_KEY = 'yada:activeWorkspaces';
const CURRENT_WORKSPACE_KEY = 'yada:currentWorkspaceId';

/**
 * Get all active workspaces
 */
export function getActiveWorkspaces(): Map<string, ActiveWorkspace> {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return new Map();

  try {
    const data = JSON.parse(raw);
    return new Map(Object.entries(data));
  } catch {
    return new Map();
  }
}

/**
 * Set active workspaces
 */
export function setActiveWorkspaces(workspaces: Map<string, ActiveWorkspace>): void {
  const obj = Object.fromEntries(workspaces);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
}

/**
 * Add or update an active workspace
 */
export function addActiveWorkspace(workspace: ActiveWorkspace): void {
  const workspaces = getActiveWorkspaces();
  workspaces.set(workspace.workspaceId, {
    ...workspace,
    lastAccessed: Date.now(),
  });
  setActiveWorkspaces(workspaces);
}

/**
 * Remove an active workspace
 */
export function removeActiveWorkspace(workspaceId: string): void {
  const workspaces = getActiveWorkspaces();
  workspaces.delete(workspaceId);
  setActiveWorkspaces(workspaces);
}

/**
 * Update workspace auth state
 */
export function updateWorkspaceAuthState(
  workspaceId: string,
  authState: WorkspaceAuthState
): void {
  const workspaces = getActiveWorkspaces();
  const workspace = workspaces.get(workspaceId);
  if (workspace) {
    workspace.authState = authState;
    workspaces.set(workspaceId, workspace);
    setActiveWorkspaces(workspaces);
  }
}

/**
 * Get current workspace ID (for UI context)
 */
export function getCurrentWorkspaceId(): string | null {
  return localStorage.getItem(CURRENT_WORKSPACE_KEY);
}

/**
 * Set current workspace ID (for UI context)
 *
 * Dispatches a 'yada:workspace:changed' event so the SSE connection
 * can reconnect to the new workspace immediately.
 */
export function setCurrentWorkspaceId(workspaceId: string | null): void {
  const previousWorkspaceId = getCurrentWorkspaceId();

  if (workspaceId) {
    localStorage.setItem(CURRENT_WORKSPACE_KEY, workspaceId);

    // Update lastAccessed timestamp
    const workspaces = getActiveWorkspaces();
    const workspace = workspaces.get(workspaceId);
    if (workspace) {
      workspace.lastAccessed = Date.now();
      workspaces.set(workspaceId, workspace);
      setActiveWorkspaces(workspaces);
    }

    // Notify the sync subsystem so it can start/switch the SSE connection.
    // Only fires when workspace actually changes to avoid redundant reconnects.
    if (previousWorkspaceId !== workspaceId) {
      window.dispatchEvent(
        new CustomEvent('yada:workspace:activated', { detail: { workspaceId } })
      );
    }
  } else {
    localStorage.removeItem(CURRENT_WORKSPACE_KEY);

    if (previousWorkspaceId) {
      window.dispatchEvent(
        new CustomEvent('yada:workspace:activated', { detail: { workspaceId: null } })
      );
    }
  }
}

/**
 * Get all workspaces for a specific profile
 */
export function getWorkspacesForProfile(profileId: string): ActiveWorkspace[] {
  const workspaces = getActiveWorkspaces();
  return Array.from(workspaces.values()).filter(
    w => w.profileId === profileId
  );
}

/**
 * Get authenticated workspaces only
 */
export function getAuthenticatedWorkspaces(): ActiveWorkspace[] {
  const workspaces = getActiveWorkspaces();
  return Array.from(workspaces.values()).filter(
    w => w.authState === 'authenticated'
  );
}

/**
 * Check if a workspace is active and authenticated
 */
export function isWorkspaceAuthenticated(workspaceId: string): boolean {
  const workspaces = getActiveWorkspaces();
  const workspace = workspaces.get(workspaceId);
  return (workspace?.authState ?? '') === 'authenticated';
}

/**
 * Migrate from legacy activeWorkspaceId to new activeWorkspaces map
 */
export function migrateLegacyWorkspaceId(): void {
  // Check if already migrated
  if (localStorage.getItem(STORAGE_KEY)) {
    return; // Already using new format
  }

  // Check for legacy activeWorkspaceId
  const legacyId = localStorage.getItem('yada:activeWorkspaceId');
  if (!legacyId) {
    return; // Nothing to migrate
  }

  console.log('[workspaceContext] Migrating legacy activeWorkspaceId:', legacyId);

  // Get current profile
  const profileId = localStorage.getItem('yada:account:activeProfileId');
  if (!profileId) {
    console.warn('[workspaceContext] Cannot migrate: no active profile');
    return;
  }

  // Create new workspace entry
  const workspace: ActiveWorkspace = {
    workspaceId: legacyId,
    profileId,
    authState: 'authenticated', // Assume authenticated if it was active
    lastAccessed: Date.now(),
  };

  const workspaces = new Map();
  workspaces.set(legacyId, workspace);
  setActiveWorkspaces(workspaces);

  // Set as current
  setCurrentWorkspaceId(legacyId);

  console.log('[workspaceContext] Migration complete');
}

/**
 * Clear all active workspaces (used during logout)
 * Note: Only clears from activeWorkspaces map, doesn't delete IndexedDB documents
 */
export function clearActiveWorkspaces(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(CURRENT_WORKSPACE_KEY);
}
